import { createClient } from '@supabase/supabase-js';
import { getParentEmail, saveParentEmail } from './email.js';

export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
export const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

let _supabase = null;
export function getSupabase() {
  if (_supabase) return _supabase;
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    console.warn('Missing Supabase environment variables.');
    return null;
  }
  _supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
  return _supabase;
}

export async function supaSignUp(email, password) {
  const sb = getSupabase();
  if (!sb) return { error: { message: 'Supabase not loaded' } };
  const { data, error } = await sb.auth.signUp({ email, password });
  if (error) return { error };
  return { data };
}

export async function supaSignIn(email, password) {
  const sb = getSupabase();
  if (!sb) return { error: { message: 'Supabase not loaded' } };
  const { data, error } = await sb.auth.signInWithPassword({ email, password });
  if (error) return { error };
  return { data };
}

export async function supaSignOut() {
  const sb = getSupabase();
  if (!sb) return;
  await sb.auth.signOut();
}

export async function supaSignInWithGoogle() {
  const sb = getSupabase();
  if (!sb) return { error: { message: 'Supabase not loaded' } };
  try { localStorage.setItem('rq:oauthPending', '1'); } catch {}
  const { data, error } = await sb.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin + '/app/', scopes: 'openid email' }
  });
  return { data, error };
}

export async function supaGetSession() {
  const sb = getSupabase();
  if (!sb) return null;
  const { data } = await sb.auth.getSession();
  return data?.session || null;
}

export async function supaGetUser() {
  const sb = getSupabase();
  if (!sb) return null;
  const { data } = await sb.auth.getUser();
  return data?.user || null;
}

export async function supaResetPassword(email) {
  const sb = getSupabase();
  if (!sb) return { error: { message: 'Supabase not loaded' } };
  const { error } = await sb.auth.resetPasswordForEmail(email, {
    redirectTo: 'https://finleyreads.com/app?type=recovery',
  });
  return { error };
}

export async function supaUpdatePassword(newPassword) {
  const sb = getSupabase();
  if (!sb) return { error: { message: 'Supabase not loaded' } };
  const { error } = await sb.auth.updateUser({ password: newPassword });
  return { error };
}

export async function syncToSupabase(profiles, settings, parentPin) {
  const sb = getSupabase();
  if (!sb) return;
  const session = await supaGetSession();
  if (!session) return;
  try {
    let { data: families } = await sb.from('families').select('id').limit(1);
    if (!families || !families.length) {
      const user = await supaGetUser();
      if (!user) return;
      const { data: newFam } = await sb.from('families').insert({ user_id: user.id, parent_pin: parentPin, timer_min: settings.tmr }).select('id').single();
      if (!newFam) return;
      families = [newFam];
    }
    const familyId = families[0].id;
    const user = await supaGetUser();
    const parentEmail = (user?.email || getParentEmail() || '').trim() || null;
    const parentFirstName = (() => {
      const fullName = (user?.user_metadata?.full_name || user?.user_metadata?.name || '').trim();
      if (!fullName) return null;
      return fullName.split(/\s+/)[0] || null;
    })();
    const notificationPrefs = settings?.notificationPrefs || {};

    await sb.from('families').update({
      parent_pin: parentPin,
      timer_min: settings.tmr,
      finley_choose: settings.finleyChoose !== false
    }).eq('id', familyId);
    // Best-effort: newer weekly email fields may not exist on older schemas yet.
    await sb.from('families').update({
      parent_email: parentEmail,
      parent_first_name: parentFirstName,
      notification_prefs: notificationPrefs
    }).eq('id', familyId).catch(() => {});
    for (const p of profiles) {
      const { data: existing } = await sb.from('children')
        .select('id').eq('family_id', familyId).eq('name', p.name).limit(1);
      let childId;
      if (existing && existing.length > 0) {
        childId = existing[0].id;
        await sb.from('children').update({
          age: p.age, interests: p.interests || [], total_stars: p.totalStars || 0,
          confidence: p.conf || 'getting_there', focus: p.focus || 'all'
        }).eq('id', childId);
      } else {
        const { data: newChild } = await sb.from('children').insert({
          family_id: familyId, name: p.name, age: p.age,
          interests: p.interests || [], total_stars: p.totalStars || 0,
          confidence: p.conf || 'getting_there', focus: p.focus || 'all'
        }).select('id').single();
        if (newChild) childId = newChild.id;
      }
      if (!childId) continue;
      const t = p.tracker;
      await sb.from('trackers').upsert({
        child_id: childId, current_level: t.currentLevel,
        streak: t.streak, rounds: t.rounds,
        rounds_at_level: t.roundsAtLevel || 0,
        bubble_unlocked: t.bubbleUnlocked || false,
        chill_mode: t.chillMode || false,
        weak_words: t.weakWords || [],
        mode_stats: t.ms || {}
      }, { onConflict: 'child_id' });
      if (t.bkt) {
        for (const [skill, pL] of Object.entries(t.bkt)) {
          await sb.rpc('upsert_bkt', { p_child_id: childId, p_skill: skill, p_learned: pL }).catch(() => {});
        }
      }
      if (t.wordMastery) {
        const entries = Object.entries(t.wordMastery).slice(0, 50);
        for (const [word, wm] of entries) {
          await sb.rpc('upsert_word_mastery', {
            p_child_id: childId, p_word: word, p_box: wm.box || 1,
            p_exposures: wm.exposures || 0, p_correct: wm.correct || 0
          }).catch(() => {});
        }
      }
      const syncKey = 'rq:logSyncIdx:' + p.id;
      const lastSynced = parseInt(localStorage.getItem(syncKey) || '0', 10);
      const unsent = (t.log || []).filter(e => e.t > lastSynced).slice(-20);
      if (unsent.length > 0) {
        const rows = unsent.map(e => ({
          child_id: childId, mode: e.m, word: e.w || null,
          correct: e.c, ms: e.ms || 0, level: e.lv || t.currentLevel,
          guessed: e.guessed || false
        }));
        await sb.from('session_logs').insert(rows).catch(() => {});
        try { localStorage.setItem(syncKey, String(unsent[unsent.length - 1].t)); } catch {}
      }
    }
    if (user?.email) saveParentEmail(user.email);
  } catch (e) { console.warn('Supabase sync error:', e); }
}

export async function pullFromSupabase() {
  const sb = getSupabase();
  if (!sb) return null;
  const session = await supaGetSession();
  if (!session) return null;
  try {
    const { data: families } = await sb.from('families').select('*').limit(1);
    if (!families || !families.length) return null;
    const family = families[0];
    const { data: children } = await sb.from('children')
      .select('*').eq('family_id', family.id);
    if (!children || !children.length) return null;
    const profiles = [];
    for (const child of children) {
      const { data: trackerRows } = await sb.from('trackers')
        .select('*').eq('child_id', child.id).limit(1);
      const tracker = trackerRows?.[0];
      const { data: bktRows } = await sb.from('bkt_state')
        .select('skill,p_learned').eq('child_id', child.id);
      const bkt = {};
      (bktRows || []).forEach(r => { bkt[r.skill] = r.p_learned; });
      const { data: wmRows } = await sb.from('word_mastery')
        .select('word,box,last_seen,exposures,correct').eq('child_id', child.id);
      const wordMastery = {};
      (wmRows || []).forEach(r => {
        wordMastery[r.word] = { box: r.box, lastSeen: new Date(r.last_seen).getTime(), exposures: r.exposures, correct: r.correct };
      });
      const { data: logRows } = await sb.from('session_logs')
        .select('mode,word,correct,ms,level,guessed,created_at')
        .eq('child_id', child.id).order('created_at', { ascending: false }).limit(50);
      const log = (logRows || []).map(r => ({
        m: r.mode, w: r.word, c: r.correct, ms: r.ms, lv: r.level,
        guessed: r.guessed, t: new Date(r.created_at).getTime()
      })).reverse();
      profiles.push({
        id: child.id, name: child.name, age: child.age,
        interests: child.interests || [],
        conf: child.confidence || 'getting_there',
        focus: child.focus || 'all',
        totalStars: child.total_stars || 0,
        tracker: {
          currentLevel: tracker?.current_level || 3,
          streak: tracker?.streak || 0,
          ms: tracker?.mode_stats || {phonics:{c:0,t:0},sight:{c:0,t:0},stories:{c:0,t:0},bubbles:{c:0,t:0}},
          weakWords: tracker?.weak_words || [],
          wordMastery,
          rounds: tracker?.rounds || 0,
          roundsAtLevel: tracker?.rounds_at_level || 0,
          bubbleUnlocked: tracker?.bubble_unlocked || false,
          log, aiProfile: null, bkt: Object.keys(bkt).length > 0 ? bkt : null,
          chillMode: tracker?.chill_mode || false,
          sessionQuality: [], levelHistory: []
        }
      });
    }
    return {
      profiles,
      parentPin: family.parent_pin || null,
      settings: {
        tmr: family.timer_min || 15,
        finleyChoose: family.finley_choose !== false,
        notificationPrefs: family.notification_prefs || {}
      }
    };
  } catch (e) { console.warn('Supabase pull error:', e); return null; }
}

export async function getSubscriptionStatus() {
  const sb = getSupabase();
  if (!sb) return null;
  try {
    const { data } = await sb.rpc('get_subscription_status');
    return data;
  } catch { return null; }
}
